import type { BaseQueryApi } from '@reduxjs/toolkit/dist/query/baseQueryTypes';
import type {
    FetchArgs,
    FetchBaseQueryError,
    FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query/react';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { getConfig } from '@klpkonsern/appconfig';
import { AuthService } from '@klpkonsern/auth-core';
import { StackdriverLogger } from '@klpkonsern/stackdriver-logger';

import { setRemoteSessionExpired } from '../store';
import type { IApiStore } from '../store';

const baseQuery = fetchBaseQuery({
    baseUrl: getConfig('WEBAPP_API_URL', '/'),
    mode: 'cors',
    prepareHeaders: async (headers) => {
        const user = await AuthService.getUser();
        if (user) {
            headers.set('Authorization', `Bearer ${user.access_token}`);
        }
        return headers;
    },
});

const baseQueryWithErrorHandling = async (
    args: string | FetchArgs,
    api: BaseQueryApi,
    extraOptions: object,
) => {
    const {
        customApi: { remoteSessionExpired },
    } = api.getState() as IApiStore;

    // Prevent API calls if remote session is already expired
    if (remoteSessionExpired) {
        console.warn('Preventing API request due to expired session:', args);
        return {
            error: { status: 401, data: 'Session expired, blocking API calls' },
        };
    }

    const start = Date.now();
    const stack = new Error().stack;

    const result = await baseQuery(args, api, extraOptions);

    const duration = Date.now() - start;

    if (result?.error) {
        const err = result.error as FetchBaseQueryError;

        // Fetch errors are not handled by the API, so we handle them here
        if (err.status === 'FETCH_ERROR') {
            const errorMessage = `A network error occurred. This could be a CORS issue or a problem with the network connection. Check the browser's console for more details.`;

            const logContext = {
                endpoint: api.endpoint,
                type: api.type,
                url: typeof args === 'string' ? args : args.url,
                durationMs: duration,
                error: err.error,
                stack,
            };

            const structuredError = new Error(errorMessage, {
                cause: { logContext },
            });
            StackdriverLogger.log(structuredError);

            return result;
        }

        const status =
            typeof err.status === 'number'
                ? err.status
                : err.status === 'PARSING_ERROR'
                  ? err.originalStatus
                  : undefined;
        const payload = err.data as { message?: string } | string | null;
        let msg: string;

        if (typeof payload === 'string') {
            msg = payload || `Request failed (${status})`;
        } else if (
            payload &&
            typeof payload === 'object' &&
            'message' in payload &&
            typeof payload.message === 'string'
        ) {
            msg = payload.message;
        } else {
            msg = `Request failed (${status})`;
        }

        // Handle 401 Unauthorized errors
        if (status === 401) {
            const meta = (result.meta as FetchBaseQueryMeta) || {};
            const wwwAuth =
                meta.response?.headers.get('www-authenticate') ?? '';
            const body = typeof err.data === 'object' ? err.data : {};
            const desc = (body as any).error_description as string;

            if (
                wwwAuth.toLowerCase().includes('invalid_token') ||
                desc?.toLowerCase().includes('invalid_token')
            ) {
                const errorMessage =
                    'Missing or invalid scope on access token. Please configure your .env with the correct audience/scope.';

                const logContext = { args, duration, stack, wwwAuth, desc };
                StackdriverLogger.log(
                    new Error(errorMessage, { cause: logContext }),
                );

                return { error: { status: 401, data: errorMessage } };
            }

            try {
                // Try to refresh token silently
                await AuthService.refreshTokenSilently();
                // Try to call the API one more time
                const retryResult = await baseQuery(args, api, extraOptions);
                if (retryResult?.error) {
                    api.dispatch(setRemoteSessionExpired(true));
                }
                return retryResult;
            } catch (error) {
                api.dispatch(setRemoteSessionExpired(true));
                return { error: { status: 401, data: msg } };
            }
        }

        // Handle other 4xx Client Errors
        if (status && status >= 400 && status < 500) {
            const logContext = {
                endpoint: api.endpoint,
                type: api.type,
                url: typeof args === 'string' ? args : args.url,
                method:
                    typeof args === 'object' && 'method' in args
                        ? args.method
                        : 'GET',
                params: (args as FetchArgs).params,
                body: (args as FetchArgs).body,
                durationMs: duration,
                statusCode: status,
                rawStatus: err.status,
                stack,
            };

            const structuredError = new Error(msg, {
                cause: {
                    logContext,
                },
            });
            StackdriverLogger.log(structuredError);
        }

        // Handle 5xx Server Errors
        if (status && status >= 500) {
            const errorMessage =
                'A server error occurred. Please try again later.';
            const structuredError = new Error(errorMessage, {
                cause: result?.error?.status,
            });

            StackdriverLogger.log(structuredError);
        }
    }

    return result;
};

export const baseApi = createApi({
    baseQuery: baseQueryWithErrorHandling,
    tagTypes: [
        'Frontpage',
        'Faktura',
        'Bygg',
        'ByggDokument',
        'ByggKontrakt',
        'ByggKunder',
        'ByggMoterom',
        'LeaseAgreement',
        'Bimsync',
        'Kunde',
        'Client',
        'User',
        'UserList',
        'CustomerUsers',
        'Message',
        'MessageList',
        'MessageBoardSummary',
        'ByggKunde',
        'Sak',
        'SakList',
        'SakStatusSummary',
        'SakForsidefeed',
        'Sakskategori',
        'SakskategoriByType',
        'File',
    ],

    endpoints: () => ({}),
});
